import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { saveAs } from 'file-saver';
import { toast } from 'react-hot-toast';
import { useOutletContext, Link } from 'react-router-dom';
import { 
    Trash2, CheckSquare, Square, 
    Folder, Image as ImageIcon, X,
    Search, Star, SortAsc, Download,
    Lock, Plus, Images, RefreshCw,
    AlertCircle, CheckCircle
} from 'lucide-react';

import ImageViewerModal from '../../components/ImageViewerModal';
import { BACKEND_URL } from '../../supabaseClient';
import './MockupGallery.css'; // ★ NEW CSS FILE

Modal.setAppElement('#root');
const MOCKUPS_PER_PAGE = 24; 

// --- FOLDER LIMITS CONFIG ---
const FOLDER_LIMITS = { starter: 0, lite: 0, pro: 10, agency: 50 };

// --- SUB-COMPONENT: CUSTOM ACTION MODAL ---
const GalleryActionModal = ({ isOpen, onClose, config }) => {
    if (!isOpen || !config) return null;
    const { type, title, message, onConfirm, confirmText, tierInfo } = config;
    
    // Icon Selection
    let icon = <AlertCircle size={32} />;
    let themeClass = 'info';
    if (type === 'delete' || type === 'destroy') { icon = <Trash2 size={32} />; themeClass = 'danger'; } 
    else if (type === 'limit') { icon = <Lock size={32} />; themeClass = 'warning'; }

    return (
        <div className="custom-modal-overlay" onClick={onClose}>
            <div className="custom-modal-content" onClick={e => e.stopPropagation()}>
                <div className="custom-modal-header">
                    <div className={`icon-badge ${themeClass}`}>{icon}</div>
                    <div className="header-text">
                        <h3>{title}</h3>
                        {tierInfo && <span className="tier-tag">{tierInfo}</span>}
                    </div>
                </div>
                <div className="custom-modal-body">
                    <p>{message}</p>
                </div>
                <div className="custom-modal-footer">
                    <button className="btn-modal-cancel" onClick={onClose}>Cancel</button>
                    {onConfirm && (
                        <button 
                            className={`btn-modal-confirm ${themeClass}`} 
                            onClick={() => { onConfirm(); onClose(); }}
                        >
                            {confirmText || 'Confirm'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const MockupGallery = ({ session }) => {
  const contextData = useOutletContext(); 
  const profile = contextData?.profile || null;

  // --- STATE ---
  const [mockups, setMockups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState([]);
  const [currentTab, setCurrentTab] = useState('all'); 
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest'); 
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [modalIndex, setModalIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Rename State
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  const [actionModal, setActionModal] = useState({ isOpen: false, config: null });
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  // --- LIMITS ---
  const tier = (profile?.subscription_tier || 'starter').toLowerCase();
  const isStarter = tier === 'starter';
  const isLite = tier === 'lite'; 
  const folderLimit = FOLDER_LIMITS[tier] || 0;
  const usedFolders = folders.length;

  // --- FETCH DATA ---
  useEffect(() => {
    if (session?.access_token && !isStarter) { fetchFolders(); fetchMockups(true); } else { setLoading(false); }
  }, [session?.access_token, currentTab, sortBy, isStarter]); 

  useEffect(() => {
      if (!isStarter) { const timeoutId = setTimeout(() => fetchMockups(true), 500); return () => clearTimeout(timeoutId); }
  }, [searchQuery]);

  const fetchFolders = async () => {
    try { const res = await fetch(`${BACKEND_URL}/api/gallery/folders`, { headers: { 'Authorization': `Bearer ${session.access_token}` }}); if (res.ok) setFolders(await res.json()); } catch (e) { console.error("Folder error", e); }
  };

  const fetchMockups = async (reset = false) => {
    if (reset) setLoading(true);
    const page = reset ? 1 : currentPage;
    let url = `${BACKEND_URL}/api/gallery/mockups?limit=${MOCKUPS_PER_PAGE}&page=${page}&sort=${sortBy}`;
    if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
    if (currentTab === 'bin') url += `&bin=true`; 
    else if (currentTab === 'favorites') url += `&favorites=true`;
    else if (currentTab !== 'all') url += `&folder_id=${currentTab}`;

    try {
        const res = await fetch(url, { headers: { 'Authorization': `Bearer ${session.access_token}` }});
        const data = await res.json();
        let fetchedMockups = data.mockups || [];
        if (reset) { setMockups(fetchedMockups); setCurrentPage(2); } 
        else { setMockups(prev => [...prev, ...fetchedMockups]); setCurrentPage(prev => prev + 1); }
        setTotalCount(data.total || 0); setHasMore(fetchedMockups.length === MOCKUPS_PER_PAGE);
    } catch (err) { if (currentTab !== 'bin') toast.error("Failed to load images"); } finally { setLoading(false); setIsLoadingMore(false); }
  };

  const triggerModal = (config) => setActionModal({ isOpen: true, config });
  const closeModal = () => setActionModal({ isOpen: false, config: null });

  // --- SELECTION LOGIC ---
  const handleCardClick = (e, id, idx) => {
      if (isSelectMode) handleSelectItem(e, id); else openModal(idx);
  };

  const handleSelectItem = (e, id) => {
      e?.stopPropagation();
      const newSet = new Set(selectedIds);
      if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
      setSelectedIds(newSet);
      if (!isSelectMode && newSet.size > 0) setIsSelectMode(true);
      if (isSelectMode && newSet.size === 0) setIsSelectMode(false);
  };

  const handleSelectAll = () => { if (selectedIds.size === mockups.length) setSelectedIds(new Set()); else setSelectedIds(new Set(mockups.map(m => m.id))); };
  const toggleSelectMode = () => { setIsSelectMode(!isSelectMode); setSelectedIds(new Set()); };

  // --- ACTIONS ---
  const handleToggleFavorite = async (e, mockup) => {
      e?.stopPropagation(); 
      if (isLite) { triggerModal({ type: 'limit', title: 'Favorites Locked', message: 'Favorites are for Pro/Agency.', confirmText: 'Upgrade', tierInfo: 'Lite Plan', onConfirm: () => window.location.href = '/pricing' }); return; }
      
      const newStatus = !mockup.is_favorite;
      setMockups(prev => prev.map(m => m.id === mockup.id ? { ...m, is_favorite: newStatus } : m));
      if (currentTab === 'favorites' && !newStatus) setMockups(prev => prev.filter(m => m.id !== mockup.id));
      
      try { await fetch(`${BACKEND_URL}/api/gallery/mockups/${mockup.id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ is_favorite: newStatus }) }); } catch (err) { toast.error("Failed"); }
  };

  const handleRename = async () => {
      if (!renameValue.trim()) return;
      const targetId = mockups[modalIndex].id;
      setMockups(prev => prev.map(m => m.id === targetId ? { ...m, name: renameValue } : m));
      setIsRenaming(false); 
      try { await fetch(`${BACKEND_URL}/api/gallery/mockups/${targetId}`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ name: renameValue }) }); toast.success("Renamed"); } catch (e) { toast.error("Rename failed"); }
  };

  const handleSoftDelete = (ids = []) => {
      const targetIds = ids.length > 0 ? ids : Array.from(selectedIds);
      if (targetIds.length === 0) return;
      triggerModal({ type: 'delete', title: 'Move to Bin?', message: `Move ${targetIds.length} items to Bin?`, confirmText: 'Move', tierInfo: `${tier.toUpperCase()}`, onConfirm: async () => {
          const toastId = toast.loading("Moving...");
          try { await Promise.all(targetIds.map(id => fetch(`${BACKEND_URL}/api/gallery/mockups/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${session.access_token}` }}))); setMockups(prev => prev.filter(m => !targetIds.includes(m.id))); setTotalCount(prev => prev - targetIds.length); setSelectedIds(new Set()); setIsSelectMode(false); toast.success("Moved", { id: toastId }); } catch (err) { toast.error("Failed", { id: toastId }); }
      }});
  };

  const handleRestore = async (ids = []) => {
      const targetIds = ids.length > 0 ? ids : Array.from(selectedIds);
      if (targetIds.length === 0) return;
      const toastId = toast.loading("Restoring...");
      try { await Promise.all(targetIds.map(id => fetch(`${BACKEND_URL}/api/gallery/mockups/${id}/restore`, { method: 'POST', headers: { 'Authorization': `Bearer ${session.access_token}` }}))); setMockups(prev => prev.filter(m => !targetIds.includes(m.id))); setTotalCount(prev => prev - targetIds.length); setSelectedIds(new Set()); setIsSelectMode(false); toast.success("Restored", { id: toastId }); } catch (err) { toast.error("Failed", { id: toastId }); }
  };

  const handlePermanentDelete = (ids = []) => {
      const targetIds = ids.length > 0 ? ids : Array.from(selectedIds);
      if (targetIds.length === 0) return;
      triggerModal({ type: 'destroy', title: 'Delete Forever?', message: `Delete ${targetIds.length} items? Irreversible.`, confirmText: 'Destroy', tierInfo: `${tier.toUpperCase()}`, onConfirm: async () => {
          const toastId = toast.loading("Deleting...");
          try { await Promise.all(targetIds.map(id => fetch(`${BACKEND_URL}/api/gallery/mockups/${id}?permanent=true`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${session.access_token}` }}))); setMockups(prev => prev.filter(m => !targetIds.includes(m.id))); setSelectedIds(new Set()); setIsSelectMode(false); toast.success("Deleted", { id: toastId }); } catch (err) { toast.error("Failed", { id: toastId }); }
      }});
  };

  const handleCreateFolder = async () => {
      if (usedFolders >= folderLimit) { triggerModal({ type: 'limit', title: 'Limit Reached', message: `Max ${folderLimit} folders on ${tier}.`, confirmText: 'Upgrade', tierInfo: `${usedFolders}/${folderLimit} Used`, onConfirm: () => window.location.href = '/pricing' }); return; }
      if (!newFolderName.trim()) return;
      setIsCreatingFolder(true);
      try { const res = await fetch(`${BACKEND_URL}/api/gallery/folders`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ name: newFolderName }) }); if (res.ok) { const newFolder = await res.json(); setFolders(prev => [...prev, newFolder].sort((a,b) => a.name.localeCompare(b.name))); setNewFolderName(''); toast.success("Created"); } } catch (e) { toast.error("Error"); } finally { setIsCreatingFolder(false); }
  };

  const handleMoveMockup = async (targetId, newFolderId) => {
      const actualId = newFolderId === 'unfiled' ? null : newFolderId;
      try { const res = await fetch(`${BACKEND_URL}/api/gallery/move`, { method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` }, body: JSON.stringify({ mockupIds: [targetId], destinationFolderId: actualId }) }); if (res.ok) { setMockups(prev => prev.filter(m => m.id !== targetId)); toast.success("Moved"); } } catch (e) { toast.error("Failed"); }
  };

  const onDragStart = (e, id) => { setDraggedItem(id); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (e, folderId) => { e.preventDefault(); if (draggedItem) { handleMoveMockup(draggedItem, folderId); setDraggedItem(null); }};
  
  const openModal = (idx) => { 
      if (isSelectMode) return; 
      setModalIndex(idx); 
      setIsModalOpen(true); 
      setIsRenaming(false); 
      setRenameValue(mockups[idx].name || '');
  };
  
  const handleModalDownload = async () => {
    if (modalIndex === null) return;
    const url = mockups[modalIndex].image_url;
    const toastId = toast.loading("Downloading...");
    try { const res = await fetch(`${BACKEND_URL}/api/download-image?url=${encodeURIComponent(url)}`); if (!res.ok) throw new Error("Failed"); const blob = await res.blob(); saveAs(blob, `${mockups[modalIndex].name || 'mockup'}.png`); toast.success("Started"); } catch (e) { toast.error("Error"); } finally { toast.dismiss(toastId); }
  };

  if (isStarter) {
      return (<div className="gallery-page-container fade-in flex-center"><div className="account-card center-card"><div className="lock-icon-circle"><Lock size={40} /></div><h2>Gallery Locked</h2><p>Available on paid plans.</p><Link to="/pricing" className="btn-primary-theme">Upgrade Now</Link></div></div>);
  }

  return (
    <div className="gallery-page-container fade-in">
      
      {/* --- 1. BANNER (Matches Dashboard) --- */}
      <div className="gallery-banner">
        <div>
          <h1>Media Gallery</h1>
          <p>Organize, view, and manage your generated designs.</p>
        </div>
        <div className="gallery-icon-circle">
            <Images size={32} />
        </div>
      </div>

      {/* --- 2. MAIN CARD --- */}
      <div className="account-card">
        
        {/* --- TOOLBAR & FOLDERS --- */}
        <div className="gallery-header-section">
            
            {/* Top Controls: Search & Sort */}
            {!isSelectMode && (
                <div className="gallery-toolbar">
                    <div className="search-wrapper">
                        <Search size={18} className="search-icon" />
                        <input type="text" placeholder="Search mockups..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="search-input" />
                    </div>
                    <div className="sort-wrapper">
                        <SortAsc size={18} className="sort-icon" />
                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
                            <option value="newest">Newest</option>
                            <option value="oldest">Oldest</option>
                            <option value="az">Name (A-Z)</option>
                            <option value="za">Name (Z-A)</option>
                        </select>
                    </div>
                </div>
            )}

            {/* Folder Tabs (Scrollable) */}
            <div className="folder-tabs-wrapper">
                <div className="folder-tabs-container">
                    <button className={`folder-tab ${currentTab === 'all' ? 'active' : ''}`} onClick={() => setCurrentTab('all')}><ImageIcon size={16} /> All</button>
                    <button className={`folder-tab ${currentTab === 'favorites' ? 'active' : ''}`} onClick={() => setCurrentTab('favorites')}><Star size={16} fill={currentTab === 'favorites' ? "#eab308" : "none"} color={currentTab === 'favorites' ? "#eab308" : "currentColor"} /> Favorites</button>
                    
                    {folders.map(f => (
                        <button key={f.id} className={`folder-tab ${currentTab === f.id ? 'active' : ''} ${draggedItem ? 'droppable-zone' : ''}`} onClick={() => setCurrentTab(f.id)} onDragOver={onDragOver} onDrop={(e) => onDrop(e, f.id)}>
                            <Folder size={16} /> {f.name}
                        </button>
                    ))}
                    
                    {currentTab !== 'bin' && (
                        <div className="folder-create-inline">
                            <input type="text" className="small-input" placeholder="New Folder" value={newFolderName} onChange={e => setNewFolderName(e.target.value)} />
                            <button onClick={handleCreateFolder} disabled={isCreatingFolder || !newFolderName} className="btn-sm-icon"><Plus size={16} /></button>
                        </div>
                    )}
                </div>
                
                <button className={`folder-tab bin-tab ${currentTab === 'bin' ? 'active' : ''}`} onClick={() => setCurrentTab('bin')}>
                    <Trash2 size={16} /> Bin
                </button>
            </div>
        </div>

        {/* --- GRID CONTENT --- */}
        <div className="account-card-content">
            {currentTab === 'bin' && (
                <div className="bin-warning">
                    <span><AlertCircle size={16} style={{display:'inline', marginRight:'6px'}}/> Items deleted automatically after 24h.</span>
                    {mockups.length > 0 && <button className="btn-text-danger" onClick={() => handlePermanentDelete(mockups.map(m => m.id))}>Empty Bin</button>}
                </div>
            )}
            
            {currentTab === 'favorites' && isLite ? (
                <div className="empty-state">
                    <div className="lock-circle"><Lock size={32} /></div>
                    <h3>Favorites are Locked</h3>
                    <p>Organize your best designs with a click. Upgrade to Pro to unlock Favorites.</p>
                    <Link to="/pricing" className="btn-primary-theme">Upgrade to Pro</Link>
                </div>
            ) : loading ? (
                <div className="empty-state"><RefreshCw size={32} className="spin" /><p>Loading gallery...</p></div> 
            ) : mockups.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-icon"><ImageIcon size={48} /></div>
                    <h3>{searchQuery ? 'No results found' : 'No images here'}</h3>
                    <p>Generate some mockups to see them here.</p>
                </div> 
            ) : (
                <div className="gallery-grid">
                    {mockups.map((m, idx) => (
                        <div key={m.id} className={`gallery-item ${selectedIds.has(m.id) ? 'selected' : ''}`} onClick={(e) => handleCardClick(e, m.id, idx)} draggable={!isSelectMode} onDragStart={(e) => onDragStart(e, m.id)}>
                            
                            {/* Checkbox (Visible on Select/Hover) */}
                            <div className={`select-checkbox ${selectedIds.has(m.id) ? 'checked' : ''} ${isSelectMode ? 'visible' : ''}`} onClick={(e) => handleSelectItem(e, m.id)}>
                                {selectedIds.has(m.id) ? <CheckSquare size={18} color="white" /> : <Square size={18} />}
                            </div>
                            
                            {/* Favorite Star (Visible on Hover) */}
                            <div className={`favorite-badge ${m.is_favorite ? 'active' : ''}`} onClick={(e) => handleToggleFavorite(e, m)}>
                                <Star size={18} fill={m.is_favorite ? "#eab308" : "rgba(0,0,0,0.2)"} color={m.is_favorite ? "#eab308" : "white"} />
                            </div>
                            
                            <div className="img-wrapper">
                                <img 
                                    src={m.thumbnail_url || m.image_url} 
                                    alt={m.name} 
                                    loading="lazy" 
                                    onError={(e) => { e.target.src = m.image_url; }} 
                                />
                                {!isSelectMode && <div className="hover-overlay"><span>View</span></div>}
                            </div>

                            <div className="img-footer">
                                <span className="img-name">{m.name || 'Untitled'}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            
            {hasMore && !loading && !(currentTab === 'favorites' && isLite) && (
                <div className="load-more-row">
                    <button className="btn-secondary" onClick={() => fetchMockups(false)} disabled={isLoadingMore}>
                        {isLoadingMore ? "Loading..." : "Load More"}
                    </button>
                </div>
            )}
        </div>
      </div>

      {/* --- FLOATING ACTION BAR --- */}
      {isSelectMode && (
          <div className="floating-action-bar">
              <span className="selection-count">{selectedIds.size} Selected</span>
              <div className="action-buttons">
                  <button onClick={handleSelectAll} className="fab-btn">Select All</button>
                  {currentTab === 'bin' ? (
                      <>
                        <button onClick={() => handleRestore()} className="fab-btn primary">Restore</button>
                        <button onClick={() => handlePermanentDelete()} className="fab-btn danger">Destroy</button>
                      </>
                  ) : (
                      <button onClick={() => handleSoftDelete()} className="fab-btn danger">Delete</button>
                  )}
                  <button onClick={toggleSelectMode} className="fab-btn cancel"><X size={18} /></button>
              </div>
          </div>
      )}

      {/* --- MODALS --- */}
      {modalIndex !== null && mockups[modalIndex] && (
        <ImageViewerModal 
            isOpen={isModalOpen} onRequestClose={() => setIsModalOpen(false)} 
            imageUrl={mockups[modalIndex].image_url} 
            imageAlt={mockups[modalIndex].name} 
            title={mockups[modalIndex].name} 
            isFavorite={mockups[modalIndex].is_favorite} 
            onToggleFavorite={(e) => handleToggleFavorite(e, mockups[modalIndex])} 
            isRenaming={isRenaming} renameValue={renameValue} 
            onStartRename={() => { setRenameValue(mockups[modalIndex].name || ''); setIsRenaming(true); }} 
            onSaveRename={handleRename} onCancelRename={() => setIsRenaming(false)} setRenameValue={setRenameValue} 
            onNext={() => modalIndex < mockups.length - 1 && setModalIndex(modalIndex + 1)} onPrevious={() => modalIndex > 0 && setModalIndex(modalIndex - 1)} 
            canGoNext={modalIndex < mockups.length - 1} canGoPrevious={modalIndex > 0} 
            folders={folders} currentFolderId={mockups[modalIndex].folder_id} 
            onMoveToFolder={(newId) => handleMoveMockup(mockups[modalIndex].id, newId)} 
            onDelete={() => { handleSoftDelete([mockups[modalIndex].id]); setIsModalOpen(false); }} 
            onDownload={handleModalDownload} 
        />
      )}
      <GalleryActionModal isOpen={actionModal.isOpen} onClose={closeModal} config={actionModal.config} />
    </div>
  );
};

export default MockupGallery;